import type { FirestoreRoomState } from '../firestore/RoomState'
import type { FirestoreSlideDeckRole } from '../firestore/SlideDeckRole'

/**
 * Assigns slide deck roles to room state users based on role constraints and priority.
 *
 * Removes role assignments for users no longer in the room and for roles that no longer exist.
 * Assigns users without roles in two passes:
 * 1. Satisfy minimum role requirements in priority order (by order field, then updatedAt, then roleName).
 * 2. Distribute remaining users one at a time to the least-filled available role,
 *    using priority order as tiebreaker, until all users are assigned or all roles are full.
 * Throws an error if all users cannot be assigned due to role constraints.
 */
export function assignSlideDeckRolesToRoomStateUsers({
  userIds,
  slideDeckRoles,
  currentRoleAssignment,
}: {
  userIds: FirestoreRoomState['userIds']
  slideDeckRoles: Map<string, FirestoreSlideDeckRole>
  currentRoleAssignment: FirestoreRoomState['assignmentRoles']
}) {
  // shallow copy current roles, return this updated object at end
  const roles = { ...(currentRoleAssignment ?? {}) }
  const userIdsSet = new Set(userIds)

  // remove any roles for users no longer in the room
  for (const userId of Object.keys(roles)) {
    // if user not in userIds, or role not in slideDeck roles, remove it
    if (!userIdsSet.has(userId) || !slideDeckRoles.has(roles[userId]))
      delete roles[userId]
  }

  // order the slide deck roles by order field -> updatedAt -> roleName
  const orderedRolesWithTotal = Array.from(slideDeckRoles.entries())
    .map(([id, role]) => {
      const totalSoFar = Object.values(roles).filter((r) => r === id).length
      return { id, totalSoFar, ...role }
    })
    .sort((a, b) => {
      // sort by order, no order goes to the end
      if (a.order !== b.order)
        return (
          (a.order ?? Number.POSITIVE_INFINITY) -
          (b.order ?? Number.POSITIVE_INFINITY)
        )
      if (a.updatedAt && b.updatedAt)
        return a.updatedAt.getTime() - b.updatedAt.getTime()
      if (a.updatedAt) return -1
      if (b.updatedAt) return 1
      return a.roleName.localeCompare(b.roleName)
    })
    .map((role, originalIndex) => ({ ...role, originalIndex }))

  // reverse the users without roles so when we pop them off we get the original order (added to room first means assigned priority role first)
  const usersWithoutRoles = userIds.filter((userId) => !roles[userId]).reverse()

  // first assign users without roles to roles in order, up to the min threshold
  for (const [
    roleIndex,
    { id: roleId, roleMin, totalSoFar },
  ] of orderedRolesWithTotal.entries()) {
    if (!usersWithoutRoles.length) break
    const toAssign = roleMin - totalSoFar
    for (let i = 0; i < toAssign; i++) {
      const userId = usersWithoutRoles.pop()
      if (!userId) break // array has been emptied

      // add role assignment and increment totalSoFar
      roles[userId] = roleId
      orderedRolesWithTotal[roleIndex].totalSoFar++
    }
  }

  // distribute remaining users one at a time to the least-filled role,
  // using original priority order as tiebreaker
  while (usersWithoutRoles.length > 0) {
    const target = orderedRolesWithTotal
      .filter((r) => r.totalSoFar < r.roleMax)
      .sort((a, b) => {
        if (a.totalSoFar !== b.totalSoFar) return a.totalSoFar - b.totalSoFar
        return a.originalIndex - b.originalIndex
      })[0]

    if (!target) break

    const userId = usersWithoutRoles.pop()!
    roles[userId] = target.id
    target.totalSoFar++
  }

  // if users without roles remain, throw an error as the room state users can not satisfy the role constraints
  if (usersWithoutRoles.length)
    throw new Error(
      `Unable to assign all users to roles, ${usersWithoutRoles.length} users remain unassigned`
    )

  // return the new role assignment
  return roles
}
